import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonModal,
  IonPage,
  IonSpinner,
  IonText,
  IonToast,
} from "@ionic/react";
import {
  addOutline,
  createOutline,
  timeOutline,
} from "ionicons/icons";

import Header from "src/components/Header";
import {
  listarRelevamientosOperativos,
  obtenerHistorialRelevamiento,
  obtenerRelevamientoOperativo,
  RelevamientoOperativoListItem,
  RelevamientoOperativoHistorialItem,
  RelevamientoOperativoDetalle,
} from "src/services/relevamientoOperativo";

interface Toast {
  show: boolean;
  msg: string;
  color?: string;
}

const RelevamientoOperativoListado: React.FC = () => {
  const history = useHistory();
  const [items, setItems] = useState<RelevamientoOperativoListItem[]>([]);
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState<Toast>({ show: false, msg: "" });

  const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
  const [historialCargando, setHistorialCargando] = useState(false);
  const [historial, setHistorial] = useState<RelevamientoOperativoHistorialItem[]>([]);
  const [actual, setActual] = useState<RelevamientoOperativoDetalle | null>(null);
  const [itemSeleccionado, setItemSeleccionado] = useState<RelevamientoOperativoListItem | null>(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const resp = await listarRelevamientosOperativos({ limit: 100 });
      setItems(resp.datos);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo listar.", color: "danger" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const verHistorial = async (item: RelevamientoOperativoListItem) => {
    setItemSeleccionado(item);
    setModalHistorialAbierto(true);
    setHistorialCargando(true);
    try {
      const [hist, det] = await Promise.all([
        obtenerHistorialRelevamiento(item.id),
        obtenerRelevamientoOperativo(item.id),
      ]);
      setHistorial(hist);
      setActual(det);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo cargar el historial.", color: "danger" });
      setHistorial([]);
      setActual(null);
    } finally {
      setHistorialCargando(false);
    }
  };

  return (
    <IonPage>
      <Header titulo="Repro" />
      <IonContent id="relevamiento-operativo" className="ion-padding">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <IonButton onClick={() => history.push("/relevamiento-operativo/nuevo")}>
            <IonIcon slot="start" icon={addOutline} />
            Nuevo Repro
          </IonButton>
        </div>

        {cargando ? (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}>
            <IonSpinner name="crescent" />
          </div>
        ) : items.length === 0 ? (
          <IonCard>
            <IonCardContent>
              <IonText color="medium">
                Todavía no registraste ningún Repro. Apretá <strong>Nuevo Repro</strong>{" "}
                para comenzar.
              </IonText>
            </IonCardContent>
          </IonCard>
        ) : (
          items.map((item) => (
            <IonCard key={item.id}>
              <IonCardHeader>
                <IonCardTitle style={{ fontSize: 16 }}>
                  {item.ps_nombre || "(sin nombre)"}{" "}
                  <IonBadge color="medium" style={{ marginLeft: 8 }}>
                    v{item.version_actual}
                  </IonBadge>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <p>
                  <strong>PS:</strong> {item.ps_codigo}
                </p>
                <p>
                  <strong>Fecha:</strong> {item.fecha_relevamiento}
                </p>
                <p>
                  <strong>Segmento Operativo:</strong> {item.segmento_operativo || "—"}
                </p>
                <p>
                  <strong>Fiscal:</strong> {item.fiscal_asignado_nombre || "—"}
                </p>
                <p>
                  <strong>Sección 3:</strong> {item.seccion_3 === "A" ? "Generales" : "Diferenciados"}
                </p>
                <p style={{ color: "#888", fontSize: 12 }}>
                  Creado: {item.creado_en}
                  {item.actualizado_en ? ` · Última edición: ${item.actualizado_en}` : ""}
                </p>
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  <IonButton
                    size="small"
                    onClick={() => history.push(`/relevamiento-operativo/editar/${item.id}`)}
                  >
                    <IonIcon slot="start" icon={createOutline} />
                    Editar
                  </IonButton>
                  <IonButton
                    size="small"
                    fill="outline"
                    onClick={() => verHistorial(item)}
                  >
                    <IonIcon slot="start" icon={timeOutline} />
                    Ver historial
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>
          ))
        )}

        {/* Modal de historial */}
        <IonModal
          isOpen={modalHistorialAbierto}
          onDidDismiss={() => {
            setModalHistorialAbierto(false);
            setHistorial([]);
            setActual(null);
          }}
        >
          <IonPage>
            <Header titulo="Historial del Repro" />
            <IonContent className="ion-padding">
              {historialCargando ? (
                <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}>
                  <IonSpinner name="crescent" />
                </div>
              ) : (
                <>
                  {itemSeleccionado && (
                    <IonCard>
                      <IonCardHeader>
                        <IonCardTitle style={{ fontSize: 16 }}>
                          {itemSeleccionado.ps_nombre} ({itemSeleccionado.ps_codigo})
                        </IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <p>
                          <strong>Versión actual:</strong> v{itemSeleccionado.version_actual}
                        </p>
                        {actual && (
                          <p style={{ color: "#666", fontSize: 12 }}>
                            <strong>Estado actual (v{actual.version_actual}):</strong> fiscal{" "}
                            {actual.fiscal_asignado_nombre || "—"}, fecha {actual.fecha_relevamiento},
                            sección {actual.seccion_3}.
                          </p>
                        )}
                      </IonCardContent>
                    </IonCard>
                  )}
                  {historial.length === 0 ? (
                    <IonCard>
                      <IonCardContent>
                        <IonText color="medium">
                          Este Repro todavía no fue editado. Aún no hay versiones previas.
                        </IonText>
                      </IonCardContent>
                    </IonCard>
                  ) : (
                    historial.map((h) => (
                      <IonCard key={h.id}>
                        <IonCardHeader>
                          <IonCardTitle style={{ fontSize: 14 }}>
                            Versión {h.version_numero} (snapshot anterior)
                          </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                          <p>
                            <strong>Editado por:</strong> {h.editado_por_usuario}{" "}
                            {h.editado_por_cargo ? `(${h.editado_por_cargo})` : ""}
                          </p>
                          <p>
                            <strong>Fecha edición:</strong> {h.editado_en}
                          </p>
                          {h.motivo && (
                            <p>
                              <strong>Motivo:</strong> {h.motivo}
                            </p>
                          )}
                          {h.snapshot && (
                            <details style={{ marginTop: 8 }}>
                              <summary style={{ cursor: "pointer", color: "#0066cc" }}>
                                Ver datos de esta versión
                              </summary>
                              <div style={{ marginTop: 8, fontSize: 13 }}>
                                <p>
                                  <strong>Fecha Repro:</strong> {h.snapshot.fecha_relevamiento}
                                </p>
                                <p>
                                  <strong>Fiscal:</strong> {h.snapshot.fiscal_asignado_nombre || "—"}
                                </p>
                                <p>
                                  <strong>Segmento Operativo:</strong> {h.snapshot.segmento_operativo || "—"}
                                </p>
                                <p>
                                  <strong>Cantidad personas:</strong> {h.snapshot.cantidad_personas}
                                  {h.snapshot.cantidad_personas_otro
                                    ? ` (${h.snapshot.cantidad_personas_otro})`
                                    : ""}
                                </p>
                                <p>
                                  <strong>Espacio residuos:</strong> {h.snapshot.tiene_espacio_residuos}
                                </p>
                                <p>
                                  <strong>Pesa residuos:</strong> {h.snapshot.pesa_residuos}
                                </p>
                                <p>
                                  <strong>Reporte residuos:</strong> {h.snapshot.reporte_residuos}
                                </p>
                                <p>
                                  <strong>Reciclable más generado:</strong>{" "}
                                  {h.snapshot.reciclable_mas_generado}
                                </p>
                                <p>
                                  <strong>Contenedores:</strong> {h.snapshot.contenedores_diferenciados}
                                </p>
                                <p>
                                  <strong>Sección:</strong> {h.snapshot.seccion_3}
                                </p>
                                {h.snapshot.material_no_cubierto && (
                                  <p>
                                    <strong>Material no cubierto:</strong>{" "}
                                    {h.snapshot.material_no_cubierto}
                                  </p>
                                )}
                                {h.snapshot.contexto_cliente && (
                                  <p>
                                    <strong>Contexto cliente:</strong> {h.snapshot.contexto_cliente}
                                  </p>
                                )}
                                {h.snapshot.multi && (
                                  <div>
                                    {Object.entries(h.snapshot.multi).map(([cat, vals]) => (
                                      <p key={cat}>
                                        <strong>{cat}:</strong>{" "}
                                        {(vals as { codigo: string }[]).map((v) => v.codigo).join(", ")}
                                      </p>
                                    ))}
                                  </div>
                                )}
                                {h.snapshot.fotos && h.snapshot.fotos.length > 0 && (
                                  <div>
                                    <strong>Fotos ({h.snapshot.fotos.length}):</strong>
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                          "repeat(auto-fill, minmax(80px, 1fr))",
                                        gap: 4,
                                        marginTop: 4,
                                      }}
                                    >
                                      {h.snapshot.fotos.map((f, idx) => (
                                        <img
                                          key={idx}
                                          src={f.url_secure}
                                          alt={`v${h.version_numero}-${idx}`}
                                          style={{
                                            width: "100%",
                                            height: 80,
                                            objectFit: "cover",
                                            border: "1px solid #ccc",
                                          }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </details>
                          )}
                        </IonCardContent>
                      </IonCard>
                    ))
                  )}
                </>
              )}
              <IonButton
                expand="block"
                color="medium"
                onClick={() => setModalHistorialAbierto(false)}
                style={{ marginTop: 16 }}
              >
                Cerrar
              </IonButton>
            </IonContent>
          </IonPage>
        </IonModal>

        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={3500}
          onDidDismiss={() => setToast({ show: false, msg: "" })}
        />
      </IonContent>
    </IonPage>
  );
};

export default RelevamientoOperativoListado;
